// /clients/log-pripsane-treninky — read-only LOG připsaných tréninků.
// Sjednocuje faktury (FAPI), hotovost (log_cash) a bonusy (log_st_bonus). Počítá se za běhu
// z PG přes /api/log-pripsane-treninky. Klik na klienta → detail klienta.
//
// 9.9.2026 (fáze 4b): sjednoceno do vzorníku. Stránka dostala obal `Stranka`
// (dřív neměla žádné okraje a hlavička seděla na hraně obrazovky), akce jsou
// ikony v hlavičce, souhrn jsou `Dlazdice`, zdroj je `Stitek` z tónů. Na
// telefonu je z tabulky seznam řádků, na počítači tabulka zůstává. Data,
// endpoint ani chování odkazu na klienta se nemění.

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { History, RefreshCw, ArrowLeft, Search, FileText, Banknote, Gift, ChevronRight } from "lucide-react";
import Card from "../components/Card.jsx";
import GradientHeader from "../components/GradientHeader.jsx";
import { Stranka, Dlazdice, Stitek, EmptyState, SkeletonRows } from "../components/ui.jsx";
import { useUzkaObrazovka } from "../lib/obrazovka.js";
import { useApiData } from "../lib/api.js";
import { formatDate as fmtDate } from "../lib/format.js";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return fmtDate(d);
}

// Zdroj tréninku. Tón jde ze vzorníku, ne z ručně psaných barev.
const ZDROJ_META = {
  Faktura:  { icon: FileText, ton: "znacka" },
  Hotovost: { icon: Banknote, ton: "ok" },
  Bonus:    { icon: Gift,     ton: "zlata" },
};

function ZdrojStitek({ zdroj }) {
  const meta = ZDROJ_META[zdroj] || ZDROJ_META.Faktura;
  return <Stitek ton={meta.ton} velikost="mala" ikona={meta.icon}>{zdroj}</Stitek>;
}

export default function LogPripsaneTreninky() {
  const navigate = useNavigate();
  const uzka = useUzkaObrazovka();
  const year = new Date().getFullYear();
  const { data, loading, refetch, refreshing } = useApiData(`/api/log-pripsane-treninky?year=${year}`);
  const [search, setSearch] = useState("");

  const rows = data?.rows || [];
  const summary = data?.summary || { pocetCelkem: 0, faktury: 0, hotovost: 0, bonusy: 0 };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.klient_name.toLowerCase().includes(q) ||
      String(r.za_co || "").toLowerCase().includes(q) ||
      r.zdroj.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const openClient = (r) => {
    if (!r.klient_name || r.klient_name.startsWith("(")) return;
    navigate(`/clients?search=${encodeURIComponent(r.klient_name)}&open=1`);
  };

  return (
    <Stranka>
      <GradientHeader
        title="Log připsaných tréninků"
        icon={History}
        loading={loading}
        akce={[
          { klic: "zpet", ikona: ArrowLeft, popis: "Zpět na klienty", onClick: () => navigate("/clients") },
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: loading || refreshing },
        ]}
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Dlazdice popisek="Celkem tréninků" hodnota={summary.pocetCelkem} ton="neutralni" />
        <Dlazdice popisek="Z faktur" hodnota={summary.faktury} ton="znacka" />
        <Dlazdice popisek="Z hotovosti" hodnota={summary.hotovost} ton="ok" />
        <Dlazdice popisek="Bonusy" hodnota={summary.bonusy} ton="zlata" />
      </div>

      <Card title={`Připsané tréninky ${year}`} ikona={History} lista odznak={`${filtered.length} z ${rows.length}`}>
        <div className="relative mb-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Hledat klienta, za co, zdroj…"
            aria-label="Hledat v logu"
            className="field pl-9"
          />
        </div>

        {loading && rows.length === 0 ? (
          <SkeletonRows rows={6} />
        ) : filtered.length === 0 ? (
          <EmptyState icon={History} title="Žádné připsané tréninky" hint={search ? "Zkus jiné hledání." : `Za rok ${year} zatím nic.`} />
        ) : uzka ? (
          /* Telefon: pět sloupců se na 390 px nevejde, proto řádky. Jméno má
             vlastní řádek a nikdy se nezkracuje, počet je vpravo velký. */
          <div className="space-y-2">
            {filtered.map((r, i) => {
              const clickable = r.klient_name && !r.klient_name.startsWith("(");
              const Obal = clickable ? "button" : "div";
              return (
                <Obal
                  key={i}
                  {...(clickable ? { type: "button", onClick: () => openClient(r) } : {})}
                  className={`polozka w-full text-left ${clickable ? "active:bg-brand-light" : ""}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-[15px] font-bold leading-snug text-slate-900 demo-sensitive">{r.klient_name}</div>
                      {r.firma && <div className="text-[12px] text-slate-500">fakturováno na {r.firma}</div>}
                    </div>
                    <span className="shrink-0 text-lg font-black tabular-nums text-emerald-700">+{r.pocet}</span>
                    {clickable && <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-400" />}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <ZdrojStitek zdroj={r.zdroj} />
                    <span className="text-[12px] font-semibold tabular-nums text-slate-500">{formatDate(r.datum)}</span>
                  </div>
                  {r.za_co && <div className="mt-1.5 text-[12.5px] leading-snug text-slate-600">{r.za_co}</div>}
                </Obal>
              );
            })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-200 text-left text-slate-500">
                  <th className="px-2 py-2.5 font-semibold">Kdy</th>
                  <th className="px-2 py-2.5 font-semibold">Komu</th>
                  <th className="px-2 py-2.5 font-semibold">Zdroj</th>
                  <th className="px-2 py-2.5 font-semibold">Za co</th>
                  <th className="px-2 py-2.5 text-right font-semibold">Počet</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const clickable = r.klient_name && !r.klient_name.startsWith("(");
                  return (
                    <tr key={i} className="border-b border-slate-100 hover:bg-brand-50/50">
                      <td className="whitespace-nowrap px-2 py-2.5 tabular-nums text-slate-600">{formatDate(r.datum)}</td>
                      <td className="px-2 py-2.5">
                        {clickable ? (
                          <button onClick={() => openClient(r)} className="inline-flex items-center gap-1 font-semibold text-brand-deep hover:underline demo-sensitive">
                            {r.klient_name} <ChevronRight className="h-3.5 w-3.5" />
                          </button>
                        ) : (
                          <span className="text-slate-500 demo-sensitive">{r.klient_name}</span>
                        )}
                        {r.firma && <div className="text-xs text-slate-400">fakturováno na {r.firma}</div>}
                      </td>
                      <td className="px-2 py-2.5"><ZdrojStitek zdroj={r.zdroj} /></td>
                      <td className="max-w-[420px] truncate px-2 py-2.5 text-slate-600" title={r.za_co}>{r.za_co}</td>
                      <td className="px-2 py-2.5 text-right font-bold tabular-nums text-emerald-700">+{r.pocet}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </Stranka>
  );
}
